import React, { useEffect, useRef, useState } from 'react';

// ANT Design
import { Select, ConfigProvider } from 'antd';
import Tooltip from '../common/Tooltip';
import { navbarElementStyle } from '../../utils/styles';
import { useKeyboard } from '../../hooks';
import { searchCaseDiatricsInsensitive } from '../../utils/general';
const { Option } = Select;

const NavbarSelector = ({
  tooltipContent,
  width,
  disabled,
  suffixIcon,
  onSelect,
  selectedValue,
  options,
  filterOption,
  customStyle = {},
  searchable = true,
  arrow = true,
  placeholder,
  allowClear,
  className,
  dropdownStyle,
  popupClassName,
  popupMatchSelectWidth,
  hideOptionTooltip = false,
  isOpen,
  onToggleOpen,
  fontSize,
  dropdownRender,
  focusNonce = null,
  ...propsOptions
}) => {
  const selectRef = useRef(null);
  // Chaque nouvelle valeur place le curseur dans la recherche (cf. SelectionBox).
  useEffect(() => {
    if (focusNonce == null) return;
    selectRef.current?.focus();
  }, [focusNonce]);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const setIsUsingKeyboard = useKeyboard(false);

  const dropdownOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  const setDropdownOpen = onToggleOpen || setInternalIsOpen;

  const select = (
    <Select
      ref={selectRef}
      className={`navbar-selector ${className ?? ''}`}
      showSearch={searchable}
      style={{
        ...navbarElementStyle,
        width: width || '120px',
        ...customStyle,
      }}
      value={selectedValue}
      disabled={disabled}
      open={dropdownOpen}
      onFocus={() => setIsUsingKeyboard(true)}
      onBlur={() => setIsUsingKeyboard(false)}
      onMouseEnter={() => tooltipContent && !dropdownOpen && setTooltipVisible(true)}
      onMouseLeave={() => tooltipContent && setTooltipVisible(false)}
      onOpenChange={(open) => {
        setDropdownOpen(open);
        if (open) {
          setTooltipVisible(false);
        }
      }}
      suffixIcon={suffixIcon !== undefined ? suffixIcon : !arrow ? null : undefined}
      placeholder={placeholder}
      optionFilterProp="children"
      onChange={(value) => {
        onSelect(value);
        setDropdownOpen(false);
      }}
      filterOption={filterOption ?? ((input, option) => {
        return searchCaseDiatricsInsensitive(input, option?.label);
      })}
      onInputKeyDown={(e) => { e.stopPropagation(); }}
      optionLabelProp="label"
      {...(dropdownRender ? { dropdownRender } : {})}
      allowClear={allowClear}
      popupMatchSelectWidth={popupMatchSelectWidth}
      popupClassName={popupClassName}
      {...(dropdownStyle ? { styles: { popup: { root: dropdownStyle } } } : {})}
    >
      {options.map(({ value, label, style, disabled, title, icon: optionIcon, extra }) => (
        <Option
          key={value}
          value={value}
          label={label}
          style={style}
          disabled={disabled}
          {...propsOptions}
          {...(hideOptionTooltip ? { title: null } : title !== undefined ? { title } : {})}
        >
          {/* `extra` : action posée en fin de ligne d'option (aperçu d'une
              section...). Mise en flex seulement quand il y en a une, pour ne
              rien changer au rendu des options ordinaires. `label` reste une
              chaîne : la recherche et l'affichage de la valeur choisie
              (optionLabelProp) continuent de fonctionner. */}
          {extra ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {optionIcon}
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {label}
              </span>
              {extra}
            </span>
          ) : (
            <>
              {optionIcon ? <span style={{ marginRight: 6 }}>{optionIcon}</span> : null}
              {label}
            </>
          )}
        </Option>
      ))}
    </Select>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ConfigProvider
        theme={{
          components: {
            Select: {
              colorTextDisabled: 'gray',
              colorBgContainerDisabled: 'white',
              // antd applique sa propre font-size (token) sur le texte
              // sélectionné ET la liste déroulante (portée par contexte React,
              // pas par cascade CSS) : un style inline sur le Select seul ne
              // suffit pas, il faut passer par le thème du composant.
              ...(fontSize ? { fontSize } : {}),
            },
          },
        }}
      >
        {tooltipContent ? (
          <Tooltip
            open={tooltipVisible}
            placement="bottom"
            mouseLeaveDelay="0"
            title={tooltipContent}
          >
            {select}
          </Tooltip>
        ) : (
          select
        )}
      </ConfigProvider>
    </div>
  );
};

export default React.memo(NavbarSelector);
